import { useSyncExternalStore } from 'react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../utils/bnFormat';
import { SEARCH_MODULES } from './globalSearchModules';
import { popupOpen } from './ModuleShortcuts';

// While Alt is held, a small key badge shows the shortcut number (Alt + 1 … 0, see ModuleShortcuts) in the corner of a
// module's tile — in Bangla digits when the app is in Bangla. The number comes from the same module list the shortcuts use,
// so it can't drift from what actually works. The parent needs `relative`; the badge sits in its top-right corner.
//
// The badge stays mounted and fades / scales in and out (so it can animate OUT too). It waits a beat before appearing, so a
// quick Alt+number doesn't flash the hints just before the page changes; it hides at once when Alt is let go.

// "Is Alt held right now?" — one shared set of window listeners, attached only while a badge is on screen.
let altDown = false;
const subscribers = new Set();
const setAlt = (down) => {
    if (down === altDown) return;
    altDown = down;
    subscribers.forEach((notify) => notify());
};
// keydown / keyup both carry the live modifier state. Alt on its own (Alt+Ctrl/Shift are other shortcuts), and not over a popup.
const onKey = (e) => setAlt(e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && !popupOpen());
const onBlur = () => setAlt(false);                // Alt+Tab and friends: the key-up goes to another window

function subscribe(notify) {
    if (subscribers.size === 0) {
        window.addEventListener('keydown', onKey, true);
        window.addEventListener('keyup', onKey, true);
        window.addEventListener('blur', onBlur);
    }
    subscribers.add(notify);
    return () => {
        subscribers.delete(notify);
        if (subscribers.size === 0) {
            window.removeEventListener('keydown', onKey, true);
            window.removeEventListener('keyup', onKey, true);
            window.removeEventListener('blur', onBlur);
            altDown = false;
        }
    };
}

const useAltHeld = () => useSyncExternalStore(subscribe, () => altDown, () => false);

export default function ModuleShortcutBadge({ id }) {
    const altHeld = useAltHeld();
    const { i18n } = useTranslation();
    const key = SEARCH_MODULES.find((m) => m.id === id)?.shortcut;
    if (!key) return null;
    const isBn = i18n.language === 'bn';
    return (
        <span
            aria-hidden="true"
            className={`pointer-events-none absolute right-0 top-0 flex h-6 min-w-6 items-center justify-center rounded-lg bg-zinc-900 px-1.5 font-bold leading-none text-white shadow-md ring-2 ring-white transition duration-200 ease-out motion-reduce:transition-none sm:-right-0.5 sm:-top-0.5 sm:h-7 sm:min-w-7 dark:bg-white dark:text-zinc-900 dark:ring-zinc-900 ${
                isBn ? 'text-[16px] sm:text-[19px]' : 'text-[13px] sm:text-[15px]'      // Bangla digits sit smaller in the font, so they get a bigger size
            } ${
                altHeld ? 'scale-100 opacity-100 delay-100' : 'scale-50 opacity-0 delay-0'
            }`}
        >
            {isBn ? toBnDigits(key) : key}
        </span>
    );
}
